import { FC, useMemo } from 'react'
import { ComposedChart, Area, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from 'recharts'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import { ArrowUpIcon } from '@heroicons/react/24/outline'
import { useAppDispatch } from 'store/hooks'
import { openModal, ModalType } from 'store/modals/modalSlice'
import { PerformanceScoreCpi } from 'models/PerformanceScoreChart'
import { dateFormat } from 'lib/utils'
import CategoryPageSelectField from '../../components/CategoryPageSelectField'
import { CPIPeriod, PerformanceData } from '../../categoryDashboard.types'
import CategoryHalfDonut from './CategoryHalfDonut'
import { PerformanceWrapper } from './performanceSection.styles'
import { SkeletonBar } from '../meta-data/metaDataSection.styles'
import { PerformanceIcon, InfoIcon, LoadingSpinner } from '../icons'
import PerformanceChartTooltip from './PerformanceChartTooltip'
import { useThemeColors } from 'hooks/useThemeColors'
import { resolveThemeColor } from 'theme/config'
import { getActiveThemeMode } from 'theme/themeMode'

dayjs.extend(weekday)

const CustomCursor = (props: any) => {
  const { points, top, height } = props
  // For weeks with no data the Area has no point, so recharts may pass only a
  // single entry in `points`. Fall back to the plot area's vertical extent so
  // the cursor still spans the chart over empty strips.
  const x = points?.[0]?.x
  if (x == null) return null
  const y1 = points?.[0]?.y ?? top ?? 0
  const y2 = points?.[1]?.y ?? (top ?? 0) + (height ?? 0)
  return <line x1={x} x2={x} y1={y1} y2={y2} stroke={resolveThemeColor('text-primary', getActiveThemeMode())} strokeWidth={1} />
}

const CustomActiveDot = (props: any) => {
  const { cx, cy } = props
  return (
    <g>
      <circle cx={cx} cy={cy} r={12} fill="white" opacity={0.5} style={{ mixBlendMode: 'lighten' }} />
      <circle
        cx={cx}
        cy={cy}
        r={5.5}
        fill={resolveThemeColor('text-primary', getActiveThemeMode())}
        stroke="black"
        strokeWidth={1}
      />
    </g>
  )
}

const PERIOD_OPTIONS = [CPIPeriod.OneMonth, CPIPeriod.ThreeMonths, CPIPeriod.SixMonths, CPIPeriod.OneYear]

interface Props {
  data?: PerformanceData
  selectedPeriod: CPIPeriod
  onTimePeriodChange: (period: CPIPeriod) => void
  performanceCpis: PerformanceScoreCpi[]
  loading?: boolean
  historyLoading?: boolean
  hasActiveCpis?: boolean
}

const PerformanceSection: FC<Props> = ({
  data,
  selectedPeriod,
  onTimePeriodChange,
  performanceCpis,
  loading,
  historyLoading,
  hasActiveCpis
}) => {
  const dispatch = useAppDispatch()
  const getColor = useThemeColors()
  // Treat a category with no active CPIs as empty regardless of what the
  // history API returned — stale or background-computed trend points
  // shouldn't render a chart for a category the user has no signal for.
  // Mirrors the equivalent guard in BenchmarkingSection.
  const isEmpty = !hasActiveCpis || !data?.trendData || data.trendData.length === 0

  const historyLastUpdate = useMemo(() => {
    const points = data?.trendData || []
    if (points.length > 0) {
      const last = points[points.length - 1].fullDate
      if (last) {
        return dayjs(last).weekday(8).format(dateFormat.lastUpdate)
      }
    }
    return ''
  }, [data?.trendData])

  const yAxis = useMemo(() => {
    const values = (data?.trendData || [])
      .map((p) => (typeof p.value === 'number' ? p.value : null))
      .filter((v): v is number => v != null && Number.isFinite(v))
    const peak = values.length > 0 ? Math.max(...values) : 0
    // Over-performing (>100%) extends the axis to 150% so the line isn't
    // clipped; otherwise keep the default 0–100% scale.
    const max = peak > 100 ? 150 : 100
    const ticks = peak > 100 ? [0, 50, 100, 150] : [0, 50, 100]
    return { max, ticks }
  }, [data?.trendData])

  const chartTicks = useMemo(
    () => (data?.trendData || []).filter((p) => p.xLabel).map((p) => p.fullDate),
    [data?.trendData]
  )
  const xLabelByDate = useMemo(
    () => new Map((data?.trendData || []).map((p) => [p.fullDate, p.xLabel])),
    [data?.trendData]
  )

  const handlePeriodChange = (e: any) => {
    onTimePeriodChange(e.target.value as CPIPeriod)
  }

  const handleOpenCalculationDetails = () => {
    dispatch(
      openModal({
        type: ModalType.calculationDetails,
        props: {
          calculationDetailsData: {
            cpis: performanceCpis,
            score: data?.score ?? 0,
            lastUpdate: data?.lastUpdate ?? ''
          }
        }
      })
    )
  }

  return (
    <PerformanceWrapper>
      <div className="performance-top">
        <div className="section-header">
          <div className="title-row">
            <PerformanceIcon className="title-icon" />
            <span className="title-text">Category Score</span>
            {!loading && performanceCpis.length > 0 && (
              <span className="info-icon-wrapper" onClick={handleOpenCalculationDetails}>
                <InfoIcon />
              </span>
            )}
          </div>
          <div className="last-update">
            Last Update:{' '}
            {data?.lastUpdate ? (
              <span className="date-value">{data.lastUpdate}</span>
            ) : (
              <SkeletonBar width="120px" sx={{ height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
            )}
          </div>
        </div>

        <div className="donut-container">
          <CategoryHalfDonut
            value={loading || !hasActiveCpis ? 0 : data?.score ?? 0}
            hideValue={loading}
            isActive={!!hasActiveCpis}
            changeIndicator={
              hasActiveCpis ? (
                <div className="change-indicator">
                  {data?.changeFromLastMonth == null ? (
                    <span className="since-label">--- since last month</span>
                  ) : (
                    <>
                      <div
                        className={`change-value ${
                          data.changeFromLastMonth > 0 || data.changeFromLastMonth === 0 ? 'positive' : 'negative'
                        }`}
                      >
                        {data.changeFromLastMonth !== 0 && <ArrowUpIcon className="arrow-icon" />}
                        <span className="change-text">{Math.abs(data.changeFromLastMonth)}%</span>
                      </div>
                      <span className="since-label">since last month</span>
                    </>
                  )}
                </div>
              ) : null
            }
          />
        </div>
      </div>

      <div className="performance-bottom">
        <div className="chart-controls">
          <CategoryPageSelectField
            options={PERIOD_OPTIONS}
            value={selectedPeriod}
            handleChange={handlePeriodChange}
            minWidth="158px"
            dropdownWidth="158px"
            disabled={loading || historyLoading}
          />
          {!loading && !historyLoading && !isEmpty && historyLastUpdate && (
            <div className="history-last-update">
              Last Update: <span className="date-value">{historyLastUpdate}</span>
            </div>
          )}
        </div>

        {loading || historyLoading ? (
          <div className="empty-state">
            <LoadingSpinner />
          </div>
        ) : isEmpty ? (
          <div className="empty-state">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 24.0002L12 15.0002L17.7419 20.7421C19.3468 17.5844 22.0056 14.9366 25.4937 13.3836L29.1479 11.7567M29.1479 11.7567L21.2262 8.71582M29.1479 11.7567L26.107 19.6784"
                stroke={getColor('dropdown-label')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {hasActiveCpis ? (
              <>
                <span className="empty-title">No Performance Data Is Available Yet</span>
                <span className="empty-description">
                  CPI is activated. The system is currently retrieving and aggregating data. Trend charts will appear
                  soon.
                </span>
              </>
            ) : (
              <>
                <span className="empty-title">No Performance Data Is Available Yet</span>
                <span className="empty-description">
                  Activate CPI on the platform to start tracking your performance and see your metrics here
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.trendData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="fullDate"
                  axisLine={false}
                  tickLine={false}
                  ticks={chartTicks}
                  interval={0}
                  tickFormatter={(value) => xLabelByDate.get(value) ?? ''}
                  tick={(props: any) => {
                    const { x, y, payload } = props
                    // Anchor the first tick to the start of the plot area and
                    // the last to the end so the labels never spill into the
                    // y-axis gutter on the left or off the chart on the right.
                    const isFirst = chartTicks[0] === payload.value
                    const isLast = chartTicks[chartTicks.length - 1] === payload.value
                    const textAnchor = isFirst ? 'start' : isLast ? 'end' : 'middle'
                    return (
                      <text
                        x={x}
                        y={y + 14}
                        textAnchor={textAnchor}
                        fill={getColor('chart-axis-text')}
                        fontSize={12}
                        fontWeight={500}
                        fontFamily="Quicksand, sans-serif"
                        letterSpacing="0.5px"
                      >
                        {xLabelByDate.get(payload.value) ?? ''}
                      </text>
                    )
                  }}
                />
                <YAxis
                  domain={[0, yAxis.max]}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: getColor('chart-axis-text'),
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'Quicksand, sans-serif',
                    letterSpacing: '0.1px'
                  }}
                  tickFormatter={(value) => `${value}%`}
                  ticks={yAxis.ticks}
                  width={46}
                />
                {yAxis.ticks
                  .filter((t) => t > 0)
                  .map((t) => (
                    <ReferenceLine key={t} y={t} stroke={getColor('chart-grid')} strokeDasharray="8 4" strokeOpacity={0.5} />
                  ))}
                <Area
                  type="linear"
                  dataKey="value"
                  stroke={getColor('cta')}
                  strokeWidth={2}
                  fill={getColor('category-perf-area-fill')}
                  fillOpacity={1}
                  activeDot={<CustomActiveDot />}
                />
                {/* Invisible series so recharts triggers the Tooltip at weeks
                    where `value` is null — otherwise hovering a missing strip
                    shows the cursor but no "No data" tooltip. */}
                <Line
                  type="linear"
                  dataKey="tooltipAnchor"
                  stroke="transparent"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  legendType="none"
                />
                <Tooltip content={<PerformanceChartTooltip />} cursor={<CustomCursor />} wrapperStyle={{ border: 'none', outline: 'none' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </PerformanceWrapper>
  )
}

export default PerformanceSection
